import { resolveFormValidationError, setErrorsToForm } from "@/utils/formUtils";
import { regexPatterns, validations } from "@/utils/validationUtils";
import { Alert, Checkbox, Col, Form, Input, Modal, Row } from "antd";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useUpdateClientUserAccount } from "../../../../../common/api/queries/client";
import HiddenInput from "../../../../../common/components/form/components/HiddenInput";
import { ModalSizes, rowGutter } from "../../../../../common/constants";
import { UUID } from "../../../../../typings/global";
import { AgentUpdateClientUserAccountWithUser, ClientUserAccountWithUserAgentView } from "../../../../user/types";

interface Props {
  clientId: UUID;
  account?: ClientUserAccountWithUserAgentView;
  onFormCancel: () => void;
}

export const UpdateClientUserAccountForm = (props: Props) => {
  const { t } = useTranslation();

  const [form] = Form.useForm<AgentUpdateClientUserAccountWithUser>();

  const updateClientUserAccount = useUpdateClientUserAccount();

  useEffect(() => {
    if (props.account) {
      form.setFieldsValue({
        optimisticLockVersion: props.account.optimisticLockVersion,
        disabled: props.account.disabled,
        user: {
          optimisticLockVersion: props.account.user.optimisticLockVersion,
          name: props.account.user.name,
          locked: props.account.user.locked,
        },
      });
    }
  }, [props.account, form]);

  const handleFormSubmit = () => {
    const accountId = props.account?.id;
    if (accountId) {
      form
        .validateFields()
        .then((values) => {
          updateClientUserAccount.mutate(
            {
              id1: props.clientId,
              id2: accountId,
              object: values,
            },
            {
              onSuccess: props.onFormCancel,
              onError: (error) => setErrorsToForm(form, "user.attrs", error.violations),
            },
          );
        })
        .catch(resolveFormValidationError);
    }
  };

  return (
    <Modal
      width={ModalSizes.TINY}
      open={!!props.account}
      title={t("user.titles.updateAccount")}
      okText={t("common.save")}
      cancelText={t("common.cancel")}
      confirmLoading={updateClientUserAccount.isPending}
      afterClose={() => form.resetFields()}
      onOk={handleFormSubmit}
      onCancel={props.onFormCancel}
      mask={{ closable: false }}
    >
      <Form form={form} layout="vertical" name="clientUserAccountUpdateForm">
        <HiddenInput name="optimisticLockVersion" />
        <HiddenInput name={["user", "optimisticLockVersion"]} />

        <Row gutter={rowGutter}>
          <Col span={24}>
            <Form.Item
              name={["user", "name"]}
              label={t("user.attrs.name")}
              rules={[validations.notNull, validations.size(1, 255), validations.pattern(regexPatterns.nameRegex)]}
            >
              <Input placeholder={t("user.helpers.userNamePlaceholder")} />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={rowGutter}>
          <Col span={12}>
            <Form.Item
              name="disabled"
              className="form-item-without-label"
              valuePropName="checked"
              initialValue={false}
              rules={[validations.none]}
            >
              <Checkbox>{t("user.attrs.disabled")}</Checkbox>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item noStyle shouldUpdate={(prev, next) => prev.user.locked !== next.user.locked}>
              {({ getFieldValue }) => (
                <Form.Item
                  name={["user", "locked"]}
                  className="form-item-without-label"
                  valuePropName="checked"
                  initialValue={false}
                  rules={[validations.none]}
                >
                  <Checkbox disabled={!getFieldValue(["user", "locked"])}>{t("user.attrs.user.locked")}</Checkbox>
                </Form.Item>
              )}
            </Form.Item>
          </Col>
        </Row>
      </Form>

      {props.account?.confirmed && <Alert title={t("user.helpers.updateInfo")} type="info" showIcon />}
    </Modal>
  );
};
